import React, { useEffect, useMemo, useState } from "react";
import { getBuilders, startScan, exportBuildersUrl, deleteBuilder } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { SectionTitle, PrimaryButton, SecondaryButton, EmptyState } from "../components/UIKit";
import { Lightning, DownloadSimple, MagnifyingGlass, TrashSimple, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const SPECIALTIES = ["all", "residential", "commercial", "utility", "mixed"];

export default function BuildersPage() {
  const { selected, countries } = useCountry();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [specialty, setSpecialty] = useState("all");
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const country = countries.find((c) => c.code === selected);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getBuilders({ country_code: selected, q: q || undefined, specialty });
      setItems(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 6000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, specialty]);

  const runScan = async () => {
    setStarting(true);
    try {
      const r = await startScan("builders", selected);
      if (r.already_running) toast.info(`Scan already running for ${country?.name}.`);
      else toast.success(`Scan started for ${country?.flag} ${country?.name}.`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start scan");
    }
    setStarting(false);
  };

  const remove = async (id) => {
    await deleteBuilder(id);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline={`${country?.flag || ""} ${country?.name || "COUNTRY"}`} title="Solar Installers">
        <PrimaryButton testId="builders-scan-btn" onClick={runScan} disabled={starting}>
          <Lightning size={14} weight="bold" className="inline mr-1.5" />
          {starting ? "STARTING…" : "SCAN MORE"}
        </PrimaryButton>
        <SecondaryButton testId="builders-export-btn" as="a" href={exportBuildersUrl(selected)}>
          <DownloadSimple size={14} weight="bold" /> EXPORT CSV
        </SecondaryButton>
      </SectionTitle>

      {/* Filters */}
      <div className="border border-black bg-white p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] border border-black/25 px-2 py-1">
          <MagnifyingGlass size={14} weight="bold" />
          <input
            data-testid="builders-search"
            placeholder="Search name, city, notes…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchData()}
            className="w-full outline-none text-sm"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {SPECIALTIES.map((s) => (
            <button
              key={s}
              data-testid={`filter-specialty-${s}`}
              onClick={() => setSpecialty(s)}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border transition-colors duration-150 ${
                specialty === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/25 hover:bg-black/5"
              }`}
              style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="text-xs text-[#525252] tabular-nums ml-auto" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          {filtered.length} results
        </div>
      </div>

      {loading && !items.length ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Buildings size={22} weight="bold" />}
          title={`No installers yet in ${country?.name || "this country"}`}
          hint="Kick off a scan — the bot will search from multiple angles (cities, specialties) until the well runs dry. You can close this tab."
          action={
            <PrimaryButton testId="empty-scan-builders" onClick={runScan} disabled={starting}>
              <Lightning size={14} weight="bold" className="inline mr-1.5" /> RUN FIRST SCAN
            </PrimaryButton>
          }
        />
      ) : (
        <div className="border border-black bg-white overflow-x-auto" data-testid="builders-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-black text-left">
                {["Name", "City", "Specialty", "Website", "Phone", "Email", "Size", "Actions"].map((h) => (
                  <th key={h} className="py-2 px-3 text-[10px] tracking-[0.2em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} data-testid={`builder-row-${b.id}`} className="border-b border-black/10 last:border-b-0 hover:bg-black/[0.02]">
                  <td className="py-2 px-3 sticky left-0 bg-white" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>{b.name}</td>
                  <td className="py-2 px-3">{b.city || "—"}</td>
                  <td className="py-2 px-3">
                    <span className="inline-block border border-black/25 px-1.5 py-0.5 text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                      {b.specialty || "mixed"}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    {b.website ? (
                      <a className="text-[#0047FF] underline" href={b.website} target="_blank" rel="noreferrer">
                        {b.website.replace(/^https?:\/\//, "").replace(/^www\./, "").slice(0, 32)}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3 tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{b.phone || "—"}</td>
                  <td className="py-2 px-3" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.75rem" }}>{b.email || "—"}</td>
                  <td className="py-2 px-3 text-xs uppercase">{b.size_estimate || "—"}</td>
                  <td className="py-2 px-3">
                    <button
                      data-testid={`delete-builder-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="text-[#525252] hover:text-red-600"
                      title="Delete"
                    >
                      <TrashSimple size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
